import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import CommonSidebar from "../../components/dashboard/CommonSidebar";
import { useAgent } from "../../hooks/management/useAgent";
import { useAgentManagement } from "../../hooks/management/useAgentManagement";
import {
  AlertTriangleIcon,
  CheckCircleIcon,
  PencilIcon,
  PlayIcon,
  SaveIcon,
  XIcon,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  convertUrlToBase64,
  convertFileToBase64,
} from "../../utils/imageUtils";
import { AgentSpec, AgentSchema } from "../../types/agent";
import CategorySearchSelect from "../../components/management/CategorySearchSelect";

// 생성 폼에서 사용할 스키마
const agentFormSchema = z.object({
  subject: z
    .string()
    .min(1, "주제를 입력해주세요.")
    .min(5, "주제는 최소 5자 이상 입력해주세요.")
    .max(500, "주제는 최대 500자까지 입력 가능합니다."),
  using_feedback: z
    .string()
    .min(1, "사용 피드백을 입력해주세요.")
    .min(5, "사용 피드백은 최소 5자 이상 입력해주세요.")
    .max(50000, "사용 피드백은 최대 50000자까지 입력 가능합니다."),
});

interface FormErrors {
  subject?: string;
  using_feedback?: string;
  modal?: string;
}

export default function AgentCreateTool() {
  const {
    formData,
    errors,
    setErrors,

    isLoading,
    createdAgent,
    setCreatedAgent,
    apiError,
    showUnsupportedModal,

    handleInputChange,
    createAgent,
    resetForm,

    // 테스트 관련
    testInputValues,
    isTestLoading,
    testResult,
    testError,
    handleTestInputChange,
    testAgent,
    resetTest,

    // 저장 관련
    isSavedLoading,
    savedError,
    saveAgent,

    // 수정 관련
    isEditLoading,
    editError,
    editAgent,
    resetEdit,

    // 템플릿 수정 관련
    isTemplateFixLoading,
    templateFixError,
    resetTemplatefix,
  } = useAgent();

  // 카테고리 관리 훅
  const {
    categories,
    isCategoriesLoading,
    error: categoriesError,
    fetchCategories,  
  } = useAgentManagement();

  const [isTestModalOpen, setIsTestModalOpen] = useState(false);

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [childLicense, setChildLicense] = useState<boolean>(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingAgent, setEditingAgent] = useState<{
    id: string;
    title: string;
    description: string;
    hidden: boolean;
    schema: AgentSchema;
  } | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const [isSaved, setIsSaved] = useState(false);

  // 단계별 UI 관리
  const [currentStep, setCurrentStep] = useState<'select-type' | 'create-form'>('select-type');
  const [selectedOutputType, setSelectedOutputType] = useState<string>('');

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    setIsSaved(false);
  }, [createdAgent?.agent_id]);

  // Agent 생성 완료 후 현재 단계 유지
  useEffect(() => {
    if (createdAgent && currentStep === 'create-form') {
      // Agent가 생성되었지만 현재 단계는 그대로 유지
    }
  }, [createdAgent, currentStep]);

  const openTestModal = useCallback(() => {
    setIsTestModalOpen(true);
  }, []);

  const closeTestModal = useCallback(() => {
    setIsTestModalOpen(false);
  }, []);

  // 폼 전체 유효성 검사
  const validateForm = useCallback((): boolean => {
    const trimmedData = {
      subject: formData.subject.trim(),
      using_feedback: formData.using_feedback.trim(),
    };

    const result = agentFormSchema.safeParse(trimmedData);

    if (!result.success) {
      const newErrors: FormErrors = {};

      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof FormErrors;
        if (field && !newErrors[field]) {
          newErrors[field] = error.message;
        }
      });

      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  }, [formData, setErrors]);

  // 개별 필드 유효성 검사
  const validateField = useCallback(
    (field: keyof typeof formData, value: string): string | undefined => {
      // modal 필드는 이미 선택되었으므로 검사하지 않음
      if (field === "modal") return undefined;
      
      const fieldSchema = agentFormSchema.shape[field as keyof typeof agentFormSchema.shape];
      const processedValue = value.trim();

      const result = fieldSchema.safeParse(processedValue);

      if (!result.success) {
        return result.error.errors[0]?.message;
      }

      return undefined;
    },
    []
  );

  // 출력 타입 선택 함수
  const handleSelectOutputType = useCallback((type: string) => {
    setSelectedOutputType(type);
    handleInputChange("modal", type);
    setCurrentStep('create-form');
  }, [handleInputChange]);

  // 뒤로 가기 함수
  const handleGoBack = useCallback(() => {
    setCurrentStep('select-type');
  }, []);

  // 전체 리셋 함수 (출력 타입 선택부터 다시)
  const handleFullReset = useCallback(() => {
    resetForm();
    setSelectedOutputType('');
    setCurrentStep('select-type');
    setCreatedAgent(null);
    setIsSaved(false);
  }, [resetForm, setCreatedAgent]);

  // Agent 생성 함수 
  const handleCreateAgent = useCallback(async () => {
    if (validateForm()) {
      await createAgent();
    }
  }, [validateForm, createAgent]);

  const InputKindLabel = ({ kind }: { kind: string }) => {
    switch (kind) {
      case "text":
        return (
          <span className="text-slate-400 text-xs bg-slate-700 px-2 py-1 rounded">
            텍스트
          </span>
        );
      case "select":
        return (
          <span className="text-slate-400 text-xs bg-slate-700 px-2 py-1 rounded">
            선택
          </span>
        );
      case "multiselect":
        return (
          <span className="text-slate-400 text-xs bg-slate-700 px-2 py-1 rounded">
            다중선택
          </span>
        );
      case "image":
        return (
          <span className="text-slate-400 text-xs bg-slate-700 px-2 py-1 rounded">
            이미지
          </span>
        );
      default:
        return (
          <span className="text-slate-400 text-xs bg-slate-700 px-2 py-1 rounded">
            텍스트
          </span>
        );
    }
  };

  // 테스트 폼 필드 렌더링 함수
  const renderTestField = (fieldKey: string, field: any) => {
    const value = testInputValues[fieldKey] || "";
    const isRequired =
      createdAgent?.agent_schema?.required?.includes(fieldKey) ?? false;

    switch (field.input_kind) {
      case "text":
        return (
          <div key={fieldKey} className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              {field.description}
              {isRequired && <span className="text-red-400 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleTestInputChange(fieldKey, e.target.value)}
              placeholder={
                field.placeholder || `${field.description}을 입력해주세요`
              }
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        );

      case "select":
        return (
          <div key={fieldKey} className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              {field.description}
              {isRequired && <span className="text-red-400 ml-1">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => handleTestInputChange(fieldKey, e.target.value)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">선택해주세요</option>
              {field.options?.map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );

      case "multiselect":
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div key={fieldKey} className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              {field.description}
              {isRequired && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {field.options?.map((option: string) => (
                <label
                  key={option}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleTestInputChange(fieldKey, [
                          ...selectedValues,
                          option,
                        ]);
                      } else {
                        handleTestInputChange(
                          fieldKey,
                          selectedValues.filter((v: string) => v !== option)
                        );
                      }
                    }}
                    className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-slate-300 text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case "image":
        return (
          <div key={fieldKey} className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              {field.description}
              {isRequired && <span className="text-red-400 ml-1">*</span>}
            </label>

            <div className="space-y-3">
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      const dataUrl = await convertFileToBase64(file, {
                        includeDataPrefix: true,
                        maxSize: 10 * 1024 * 1024,
                      });

                      handleTestInputChange(fieldKey, {
                        file,
                        dataUrl,
                        name: file.name,
                        size: file.size,
                      });
                    } catch (error) {
                      toast.error(
                        error instanceof Error
                          ? error.message
                          : "파일 업로드 중 오류가 발생했습니다."
                      );
                      e.target.value = "";
                    }
                  } else {
                    handleTestInputChange(fieldKey, null);
                  }
                }}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />

              {value && value.dataUrl && (
                <div className="relative">
                  <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
                    <div className="flex items-start gap-3">
                      <img
                        src={value.dataUrl}
                        alt="미리보기"
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {value.name}
                        </p>
                        <p className="text-slate-400 text-xs">
                          {(value.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleTestInputChange(fieldKey, null)}
                        className="p-1 hover:bg-slate-700 rounded transition-colors"
                      >
                        <XIcon className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!value && (
                <p className="text-slate-500 text-xs">
                  JPG, PNG, GIF 등의 이미지 파일을 선택해주세요. (최대 10MB)
                </p>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div key={fieldKey} className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              {field.description}
              {isRequired && <span className="text-red-400 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleTestInputChange(fieldKey, e.target.value)}
              placeholder={
                field.placeholder || `${field.description}을 입력해주세요`
              }
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        );
    }
  };

  // 테스트 폼 유효성 검사
  const canRunTest = useCallback(() => {
    if (!createdAgent) return false;

    const requiredFields = createdAgent.agent_schema.required;
    return requiredFields.every((field) => {
      const value = testInputValues[field];

      // 배열 타입 (multiselect)
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      // 이미지 타입
      if (value && typeof value === "object" && value.file) {
        return true;
      }

      // 일반 텍스트 타입
      return value && value.toString().trim() !== "";
    });
  }, [createdAgent, testInputValues]);

  const handleSaveAgent = useCallback(async () => {
    if (!createdAgent) {
      toast.error("저장할 Agent가 없습니다.");
      return;
    }

    if (selectedCategories.length === 0) {
      toast.error("최소 하나 이상의 카테고리를 선택해주세요.");
      return;
    }

    try {
      const success = await saveAgent(
        { url: createdAgent.agent_image },
        selectedCategories,
        childLicense
      );

      if (success) {
        setIsSaved(true);
        toast.success("Agent가 성공적으로 저장되었습니다!");
        setSelectedCategories([]);
        setChildLicense(false);
      } else {
        toast.error("Agent 저장에 실패했습니다.");
      }
    } catch (error) {
      console.error("Agent 저장 중 오류:", error);
      try {
        const b64ImageData = await convertUrlToBase64(
          createdAgent.agent_image,
          {
            includeDataPrefix: false,
          }
        );

        const success = await saveAgent(
          { b64: b64ImageData },
          selectedCategories,
          childLicense
        );

        if (success) {
          setIsSaved(true);
          toast.success("Agent가 성공적으로 저장되었습니다! (fallback 방식)");
          setSelectedCategories([]);
          setChildLicense(false);
        } else {
          toast.error("Agent 저장에 실패했습니다.");
        }
      } catch (fallbackError) {
        console.error("base64 fallback도 실패:", fallbackError);
        const errorMessage =
          fallbackError instanceof Error
            ? fallbackError.message
            : "저장 중 오류가 발생했습니다.";

        toast.error(errorMessage);
      }
    }
  }, [createdAgent, selectedCategories, childLicense, saveAgent]);

  const handleStartEdit = useCallback(async () => {
    if (!createdAgent) {
      toast.error("수정할 Agent가 없습니다.");
      return;
    }

    setIsEditMode(true);
    setCurrentQuestionIndex(0); 
    setEditingAgent({
      id: createdAgent.agent_id,
      title: createdAgent.agent_title,
      description: createdAgent.agent_description,
      hidden: false,
      schema: JSON.parse(JSON.stringify(createdAgent.agent_schema)), 
    });
  }, [createdAgent]);

  const handleCancelEdit = useCallback(() => {
    setIsEditMode(false);
    setEditingAgent(null);
    setCurrentQuestionIndex(0);
    resetEdit();
  }, [resetEdit]);

  const handlePrevQuestion = useCallback(() => {
    setCurrentQuestionIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextQuestion = useCallback(() => {
    if (!editingAgent) return;
    const maxIndex = Object.keys(editingAgent.schema.properties).length - 1;
    setCurrentQuestionIndex((prev) => Math.min(maxIndex, prev + 1));
  }, [editingAgent]);

  const handleSaveEdit = useCallback(async () => {
    if (!createdAgent || !editingAgent) {
      toast.error("수정할 Agent가 없습니다.");
      return;
    }

    if (selectedCategories.length === 0) {
      toast.error("최소 하나 이상의 카테고리를 선택해주세요.");
      return;
    }

    const confirmed = window.confirm(
      "Agent를 수정하면 템플릿이 재빌드됩니다.\n\n" +
      "재빌드 과정에서 시간이 소요될 수 있으며, " +
      "수정 내용에 따라 기존 템플릿과 다르게 생성될 수 있습니다.\n\n" +
      "계속 진행하시겠습니까?"
    );

    if (!confirmed) {
      return; 
    }

    try {
      const updatedSpec: AgentSpec = {
        ...createdAgent,
        agent_id: editingAgent.id,
        agent_title: editingAgent.title,
        agent_description: editingAgent.description,
        agent_schema: editingAgent.schema,
      };

      const result = await editAgent(
        { url: createdAgent.agent_image },
        updatedSpec,
        childLicense,
        selectedCategories,
        editingAgent.hidden
      );

      if (result.success) {
        if (result.updatedTemplate) {
          toast.success("Agent와 템플릿이 성공적으로 수정되었습니다! 🎉");
        } else {
          toast.success("Agent가 성공적으로 수정되었습니다!");
        }

        setIsEditMode(false);
        setEditingAgent(null);
        resetTemplatefix();

        setCreatedAgent((prev: AgentSpec | null) =>
          prev
            ? {
                ...prev,
                agent_title: editingAgent.title,
                agent_description: editingAgent.description,
                agent_schema: editingAgent.schema,
                ...(result.updatedTemplate && {
                  agent_template: result.updatedTemplate,
                }),
              }
            : null
        );
      } else {
        toast.error("Agent 수정에 실패했습니다.");
      }
    } catch (error) {
      console.error("Agent 수정 중 오류:", error);
      toast.error("Agent 수정에 실패했습니다.");
    }
  }, [
    createdAgent,
    editingAgent,
    selectedCategories,
    editAgent,
    childLicense,
    resetTemplatefix,
    setCreatedAgent,
  ]);

  // const handleDeployAgent = useCallback(async () => {
  //   if (!createdAgent) {
  //     toast.error("배포할 Agent가 없습니다.");
  //     return;
  //   }

  //   try {
  //     const success = await deployAgent(createdAgent.agent_id);

  //     if (success) {
  //       setIsDeployed(true);
  //       toast.success("Agent가 성공적으로 배포되었습니다! 🚀");
  //     } else {
  //       toast.error("Agent 배포에 실패했습니다.");
  //     }
  //   } catch (error) {
  //     console.error("Agent 배포 중 오류:", error);
  //     toast.error("Agent 배포 중 오류가 발생했습니다.");
  //   }
  // }, [createdAgent, deployAgent]);

  // const handleDeployCancelAgent = useCallback(async () => {
  //   if (!createdAgent) {
  //     toast.error("배포 취소할 Agent가 없습니다.");
  //     return;
  //   }

  //   try {
  //     const success = await deployCancelAgent(createdAgent.agent_id);

  //     if (success) {
  //       setIsDeployed(false);
  //       toast.success("Agent 배포가 성공적으로 취소되었습니다! 📦");
  //     } else {
  //       toast.error("Agent 배포 취소에 실패했습니다.");
  //     }
  //   } catch (error) {
  //     console.error("Agent 배포 취소 중 오류:", error);
  //     toast.error("Agent 배포 취소 중 오류가 발생했습니다.");
  //   }
  // }, [createdAgent, deployCancelAgent]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isTestModalOpen) {
        closeTestModal();
      }
    };

    if (isTestModalOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isTestModalOpen, closeTestModal]);

  // 출력 타입 선택 UI 컴포넌트
  const OutputTypeSelection = () => (
    <div className="min-h-screen bg-slate-950 lg:ml-72 flex items-center justify-center">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">AI Agent 생성툴</h1>
          <p className="text-slate-400 text-lg">
            먼저 생성하고 싶은 Agent의 출력 타입을 선택해주세요
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {/* 텍스트 모달 옵션 */}
          <div
            className="group relative bg-slate-900 rounded-2xl border border-slate-800 p-8 cursor-pointer transition-all duration-300 hover:border-blue-400 hover:shadow-2xl hover:shadow-blue-400/20 hover:-translate-y-2"
            onClick={() => handleSelectOutputType("text-modal")}
          >
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-blue-400 transition-colors">
                텍스트 모달
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-6">
                사용자의 입력을 받아 텍스트 형태의 결과를 생성하는 Agent입니다. 
                설문지, 분석 리포트, 추천 내용 등을 텍스트로 제공합니다.
              </p>
              <div className="space-y-2 text-sm text-slate-500">
                <p>✓ 텍스트 기반 결과 생성</p>
                <p>✓ 분석 및 추천 기능</p>
                <p>✓ 빠른 응답 속도</p>
              </div>
            </div>
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>

          {/* 이미지 생성 모달 옵션 */}
          <div
            className="group relative bg-slate-900 rounded-2xl border border-slate-800 p-8 cursor-pointer transition-all duration-300 hover:border-purple-400 hover:shadow-2xl hover:shadow-purple-400/20 hover:-translate-y-2"
            onClick={() => handleSelectOutputType("gen-image-modal")}
          >
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-purple-400 transition-colors">
                이미지 생성 모달
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-6">
                사용자의 입력을 바탕으로 AI가 이미지를 생성하는 Agent입니다. 
                창작물, 디자인, 일러스트 등을 이미지로 제공합니다.
              </p>
              <div className="space-y-2 text-sm text-slate-500">
                <p>✓ AI 이미지 생성</p>
                <p>✓ 창작 및 디자인 지원</p>
                <p>✓ 시각적 결과물</p>
              </div>
            </div>
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-slate-500 text-sm">
            선택한 타입에 따라 Agent의 기능과 출력 형태가 결정됩니다
          </p>
        </div>
      </div>
      <CommonSidebar />
    </div>
  );

  // 현재 단계에 따른 조건부 렌더링
  if (currentStep === 'select-type') {
    return <OutputTypeSelection />;
  }

  return (
    <div className="min-h-screen bg-slate-950 lg:ml-72">
      {/* 헤더 */}
      <div className="bg-slate-900 border-b border-slate-800 p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleGoBack}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors group"
                title="출력 타입 선택으로 돌아가기"
              >
                <svg className="w-5 h-5 text-slate-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">AI Agent 생성툴</h1>
                <p className="text-slate-400 mt-1">
                  {selectedOutputType === 'text-modal' ? '텍스트 모달' : '이미지 생성 모달'} Agent 생성
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="px-3 py-1 bg-slate-800 rounded-lg text-sm text-slate-300">
              선택된 타입: {selectedOutputType === 'text-modal' ? '텍스트 모달' : '이미지 생성 모달'}
            </div>
            <button
              onClick={handleFullReset}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors"
            >
              처음부터 다시
            </button>
          </div>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="min-h-screen p-6">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 min-h-full">
          {/* 왼쪽: 생성 폼 */}
          <div className="space-y-6">
            <div className="bg-slate-900 rounded-lg border border-slate-800 p-6 min-h-full">
              <h2 className="text-xl font-semibold text-white mb-6">
                Agent 생성
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-base font-medium text-slate-300 mb-4">
                    주제 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={formData.subject}
                    onChange={(e) =>
                      handleInputChange("subject", e.target.value)
                    }
                    onBlur={(e) => {
                      const error = validateField("subject", e.target.value);
                      if (error && !errors.subject) {
                        console.log(`Subject validation: ${error}`);
                      }
                    }}
                    placeholder="예: 아이들이 휴대폰 중독인지 검사하고 평가할 수 있는 설문지"
                    className={`w-full px-4 py-3 bg-slate-800 border rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                      errors.subject
                        ? "border-red-400 focus:ring-red-400"
                        : "border-slate-700"
                    }`}
                    rows={10}
                    disabled={isLoading}
                  />
                  {errors.subject && (
                    <p className="mt-2 text-sm text-red-400">
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-base font-medium text-slate-300 mb-4">
                    사용 피드백 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={formData.using_feedback}
                    onChange={(e) =>
                      handleInputChange("using_feedback", e.target.value)
                    }
                    placeholder="예: 아이들이 직접적으로 테스트할거라서 좀 친근하고 이해하기 쉬운 워딩이 필요"
                    className={`w-full px-4 py-3 bg-slate-800 border rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                      errors.using_feedback
                        ? "border-red-400 focus:ring-red-400"
                        : "border-slate-700"
                    }`}
                    rows={10}
                    disabled={isLoading}
                  />
                  {errors.using_feedback && (
                    <p className="mt-2 text-sm text-red-400">
                      {errors.using_feedback}
                    </p>
                  )}
                </div>


                <div className="flex gap-4 pt-4">
                  <button
                    onClick={handleCreateAgent}
                    disabled={isLoading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white py-3 px-6 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        생성 중...
                      </div>
                    ) : (
                      "Agent 생성"
                    )}
                  </button>

                  <button
                    onClick={resetForm}
                    disabled={isLoading}
                    className="px-6 py-3 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500"
                  >
                    폼 초기화
                  </button>
                </div>
              </div>
            </div>

            {/* 에러 메시지들 (왼쪽 하단) */}
            {apiError && (
              <div className="bg-red-900/50 border border-red-400 text-red-100 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <AlertTriangleIcon className="w-5 h-5 mr-2" />
                  {apiError}
                </div>
              </div>
            )}

            {showUnsupportedModal && (
              <div className="bg-yellow-900/50 border border-yellow-400 text-yellow-100 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <AlertTriangleIcon className="w-5 h-5 mr-2" />
                  현재 선택하신 모달 타입은 지원하지 않습니다. 다른 타입을
                  선택해주세요.
                </div>
              </div>
            )}
          </div>

          {/* 오른쪽: 생성 결과 */}
          <div className="space-y-6">
            {!createdAgent ? (
              <div className="bg-slate-900 rounded-lg border border-slate-800 p-12 flex flex-col items-center justify-center text-center min-h-[600px]">
                <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                  <svg
                    className="w-8 h-8 text-slate-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-slate-300 mb-2">
                  Agent를 생성해보세요
                </h3>
                <p className="text-slate-500 text-sm max-w-sm">
                  왼쪽에서 주제와 사용 피드백을 입력한 후 "Agent 생성" 버튼을
                  클릭하면 결과가 여기에 표시됩니다.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Agent 정보 */}
                <div className="bg-slate-900 rounded-lg border border-slate-800 p-6 min-h-[600px]">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon className="w-6 h-6 text-green-500" />
                      <h2 className="text-xl font-semibold text-white">
                        Agent 생성 완료
                      </h2>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={openTestModal}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <PlayIcon className="w-4 h-4" />
                        테스트하기
                      </button>
                      {isEditMode ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            disabled={isEditLoading || isTemplateFixLoading}
                            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                          >
                            {isEditLoading || isTemplateFixLoading ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                {isTemplateFixLoading
                                  ? "템플릿 재수정 중..."
                                  : "저장 중..."}
                              </>
                            ) : (
                              <>
                                <SaveIcon className="w-4 h-4" />
                                저장
                              </>
                            )}
                          </button>

                          <button
                            onClick={handleCancelEdit}
                            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            <XIcon className="w-4 h-4" />
                            취소
                          </button>
                        </>
                      ) : (
                        <>
                          {!isSaved && (
                            <button
                              onClick={handleSaveAgent}
                              disabled={isSavedLoading}
                              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                            >
                              {isSavedLoading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              ) : (
                                <SaveIcon className="w-4 h-4" />
                              )}
                              저장
                            </button>
                          )}

                          {isSaved && (
                            <>
                              <button
                                onClick={handleStartEdit}
                                className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                              >
                                <PencilIcon className="w-4 h-4" />
                                편집
                              </button>

                              {/* {isDeployed ? (
                                <button
                                  onClick={handleDeployCancelAgent}
                                  disabled={isDeployCancelLoading}
                                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                                >
                                  {isDeployCancelLoading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  ) : (
                                    <XIcon className="w-5 h-5" />
                                  )}
                                  배포취소
                                </button>
                              ) : (
                                <button
                                  onClick={handleDeployAgent}
                                  disabled={isDeployLoading}
                                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                                >
                                  {isDeployLoading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  ) : (
                                    <RocketIcon className="w-4 h-4" />
                                  )}
                                  배포
                                </button>
                              )} */}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-4">
                      <div>
                        {isEditMode ? (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-slate-300 mb-1">
                                Agent 제목
                              </label>
                              <input
                                type="text"
                                value={editingAgent?.title || ""}
                                onChange={(e) =>
                                  setEditingAgent((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          title: e.target.value,
                                        }
                                      : null
                                  )
                                }
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-300 mb-1">
                                Agent 설명
                              </label>
                              <textarea
                                value={editingAgent?.description || ""}
                                onChange={(e) =>
                                  setEditingAgent((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          description: e.target.value,
                                        }
                                      : null
                                  )
                                }
                                rows={3}
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        ) : (
                          <div>
                            <h3 className="text-lg font-medium text-white mb-2">
                              {createdAgent.agent_title}
                            </h3>
                            <p className="text-slate-300 text-sm">
                              {createdAgent.agent_description}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* 저장 설정 */}
                      {(!isSaved || isEditMode) && (
                        <div className="space-y-4 mt-6">
                          <h4 className="text-white font-medium mb-2">
                            저장 설정
                          </h4>

                          {/* 카테고리 선택 */}
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              카테고리 선택
                            </label>

                            {isCategoriesLoading ? (
                              <div className="text-slate-400 text-sm">
                                카테고리 로딩 중...
                              </div>
                            ) : categoriesError ? (
                              <div className="text-red-400 text-sm">
                                카테고리 로드 실패
                              </div>
                            ) : (
                              <CategorySearchSelect
                                categories={categories}
                                selectedCategories={selectedCategories}
                                onChange={setSelectedCategories}
                                placeholder="카테고리를 검색하거나 새로 만드세요..."
                              />
                            )}
                          </div>

                          {/* 숨김 설정 */}
                          {isEditMode && (
                          <div>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editingAgent?.hidden || false}
                                onChange={(e) =>
                                  setEditingAgent((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          hidden: e.target.checked,
                                        }
                                      : null
                                  )
                                }
                                className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-slate-300 text-sm">
                                Agent 숨김
                                </span>
                              </label>
                            </div>
                          )}

                          {/* 아동 라이선스 */}
                          <div>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={childLicense}
                                onChange={(e) =>
                                  setChildLicense(e.target.checked)
                                }
                                className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-slate-300 text-sm">
                                아동 라이선스 적용
                              </span>
                            </label>
                            <p className="text-xs text-slate-500 mt-1 ml-6">
                              아동을 대상으로 하는 Agent인 경우 체크해주세요.
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-400">Agent ID:</span>
                          <p className="text-white font-mono text-xs mt-1 break-all">
                            {createdAgent.agent_id}
                          </p>
                        </div>
                        <div>
                          <span className="text-slate-400">타입:</span>
                          <p className="text-white mt-1">
                            {createdAgent.agent_kind === "text-modal"
                              ? "텍스트 모달"
                              : "이미지 생성 모달"}
                          </p>
                        </div>
                      </div>

                      <div>
                        {isEditMode ? (
                          <div>
                            {/* 헤더와 네비게이션 */}
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-white font-medium">
                                문항 편집 (
                                {editingAgent
                                  ? Object.keys(editingAgent.schema.properties)
                                      .length
                                  : 0}
                                개)
                              </h4>

                              {editingAgent &&
                                Object.keys(editingAgent.schema.properties)
                                  .length > 0 && (
                                  <div className="flex items-center gap-2">
                                    {/* 현재 위치 표시 */}
                                    <span className="text-xs text-slate-400">
                                      {currentQuestionIndex + 1} /{" "}
                                      {
                                        Object.keys(
                                          editingAgent.schema.properties
                                        ).length
                                      }
                                    </span>

                                    {/* 네비게이션 버튼 */}
                                    <div className="flex gap-1">
                                      <button
                                        onClick={handlePrevQuestion}
                                        disabled={currentQuestionIndex === 0}
                                        className="w-8 h-8 flex items-center justify-center rounded bg-slate-700 text-white hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        title="이전 문항"
                                      >
                                        ‹
                                      </button>
                                      <button
                                        onClick={handleNextQuestion}
                                        disabled={
                                          currentQuestionIndex >=
                                          Object.keys(
                                            editingAgent.schema.properties
                                          ).length -
                                            1
                                        }
                                        className="w-8 h-8 flex items-center justify-center rounded bg-slate-700 text-white hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        title="다음 문항"
                                      >
                                        ›
                                      </button>
                                    </div>
                                  </div>
                                )}
                            </div>

                            {editingAgent &&
                            Object.keys(editingAgent.schema.properties).length >
                              0 ? (
                              (() => {
                                const questionEntries = Object.entries(
                                  editingAgent.schema.properties
                                );
                                const currentEntry =
                                  questionEntries[currentQuestionIndex];
                                if (!currentEntry) return null;

                                const [key, field] = currentEntry;
                                return (
                                  <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
                                    {/* 문항 헤더 */}
                                    <div className="mb-4">
                                      <h5 className="text-lg font-medium text-white">
                                        문항 {currentQuestionIndex + 1}
                                      </h5>
                                    </div>

                                    <div className="space-y-4">
                                      {/* 질문 내용 */}
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">
                                          질문 내용
                                        </label>
                                        <input
                                          type="text"
                                          value={field.description}
                                          onChange={(e) => {
                                            if (!editingAgent) return;
                                            setEditingAgent((prev) =>
                                              prev
                                                ? {
                                                    ...prev,
                                                    schema: {
                                                      ...prev.schema,
                                                      properties: {
                                                        ...prev.schema
                                                          .properties,
                                                        [key]: {
                                                          ...prev.schema
                                                            .properties[key],
                                                          description:
                                                            e.target.value,
                                                        },
                                                      },
                                                    },
                                                  }
                                                : null
                                            );
                                          }}
                                          className="w-full px-4 py-3 text-sm bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          placeholder="질문 내용을 입력하세요"
                                        />
                                      </div>

                                      {/* 입력 타입 */}
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">
                                          입력 타입
                                        </label>
                                        <select
                                          value={field.input_kind}
                                          onChange={(e) => {
                                            if (!editingAgent) return;
                                            const newInputKind = e.target
                                              .value as
                                              | "select"
                                              | "text"
                                              | "multiselect"
                                              | "image";
                                            setEditingAgent((prev) =>
                                              prev
                                                ? {
                                                    ...prev,
                                                    schema: {
                                                      ...prev.schema,
                                                      properties: {
                                                        ...prev.schema
                                                          .properties,
                                                        [key]: {
                                                          ...prev.schema
                                                            .properties[key],
                                                          input_kind:
                                                            newInputKind,
                                                          ...(newInputKind ===
                                                            "text" ||
                                                          newInputKind ===
                                                            "image"
                                                            ? {
                                                                options:
                                                                  undefined,
                                                              }
                                                            : {}),
                                                        },
                                                      },
                                                    },
                                                  }
                                                : null
                                            );
                                          }}
                                          className="w-full px-4 py-3 text-sm bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                          <option value="text">
                                            텍스트 입력
                                          </option>
                                          <option value="select">
                                            단일 선택
                                          </option>
                                          <option value="multiselect">
                                            다중 선택
                                          </option>
                                          <option value="image">
                                            이미지 업로드
                                          </option>
                                        </select>
                                      </div>

                                      {/* 선택형 옵션 설정 */}
                                      {(field.input_kind === "select" ||
                                        field.input_kind === "multiselect") && (
                                        <div>
                                          <label className="block text-sm font-medium text-slate-300 mb-2">
                                            선택 옵션 (콤마로 구분)
                                          </label>
                                          <input
                                            type="text"
                                            value={
                                              field.options?.join(", ") || ""
                                            }
                                            onChange={(e) => {
                                              if (!editingAgent) return;
                                              const options = e.target.value
                                                .split(",")
                                                .map((opt) => opt.trim())
                                                .filter((opt) => opt);
                                              setEditingAgent((prev) =>
                                                prev
                                                  ? {
                                                      ...prev,
                                                      schema: {
                                                        ...prev.schema,
                                                        properties: {
                                                          ...prev.schema
                                                            .properties,
                                                          [key]: {
                                                            ...prev.schema
                                                              .properties[key],
                                                            options:
                                                              options.length > 0
                                                                ? options
                                                                : undefined,
                                                          },
                                                        },
                                                      },
                                                    }
                                                  : null
                                              );
                                            }}
                                            className="w-full px-4 py-3 text-sm bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="예: 옵션1, 옵션2, 옵션3"
                                          />
                                        </div>
                                      )}

                                      {/* 텍스트 placeholder 설정 */}
                                      {field.input_kind === "text" && (
                                        <div>
                                          <label className="block text-sm font-medium text-slate-300 mb-2">
                                            입력 힌트 (placeholder)
                                          </label>
                                          <input
                                            type="text"
                                            value={field.placeholder || ""}
                                            onChange={(e) => {
                                              if (!editingAgent) return;
                                              setEditingAgent((prev) =>
                                                prev
                                                  ? {
                                                      ...prev,
                                                      schema: {
                                                        ...prev.schema,
                                                        properties: {
                                                          ...prev.schema
                                                            .properties,
                                                          [key]: {
                                                            ...prev.schema
                                                              .properties[key],
                                                            placeholder:
                                                              e.target.value ||
                                                              undefined,
                                                          },
                                                        },
                                                      },
                                                    }
                                                  : null
                                              );
                                            }}
                                            className="w-full px-4 py-3 text-sm bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="입력 힌트를 입력하세요"
                                          />
                                        </div>
                                      )}

                                      {/* 필수 여부 */}
                                      <div>
                                        <label className="flex items-center space-x-3 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={editingAgent.schema.required.includes(
                                              key
                                            )}
                                            onChange={(e) => {
                                              if (!editingAgent) return;
                                              const newRequired = e.target
                                                .checked
                                                ? [
                                                    ...editingAgent.schema
                                                      .required,
                                                    key,
                                                  ]
                                                : editingAgent.schema.required.filter(
                                                    (r) => r !== key
                                                  );
                                              setEditingAgent((prev) =>
                                                prev
                                                  ? {
                                                      ...prev,
                                                      schema: {
                                                        ...prev.schema,
                                                        required: newRequired,
                                                      },
                                                    }
                                                  : null
                                              );
                                            }}
                                            className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                                          />
                                          <span className="text-sm text-slate-300">
                                            필수 입력
                                          </span>
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()
                            ) : (
                              <div className="text-center py-8 text-slate-400">
                                <p>문항이 없습니다.</p>
                                <p className="text-sm mt-1">
                                  이 Agent에는 편집 가능한 문항이 없습니다.
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <h4 className="text-white font-medium mb-2">
                              입력 필드 (
                              {
                                Object.keys(
                                  createdAgent.agent_schema.properties
                                ).length
                              }
                              개)
                            </h4>
                            <div className="bg-slate-800 rounded-lg p-4 max-h-48 overflow-y-auto custom-scrollbar">
                              <div className="space-y-2">
                                {Object.entries(
                                  createdAgent.agent_schema.properties
                                ).map(([key, field]) => (
                                  <div
                                    key={key}
                                    className="flex items-center justify-between py-2 border-b border-slate-700 last:border-b-0"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <span className="text-white text-sm">
                                        {field.description}
                                      </span>
                                      {createdAgent.agent_schema.required.includes(
                                        key
                                      ) && (
                                        <span className="text-red-400 text-xs ml-1">
                                          *
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-slate-400 text-xs bg-slate-700 px-2 py-1 rounded ml-2 flex-shrink-0">
                                      <InputKindLabel kind={field.input_kind} />
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Agent 이미지 */}
                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Agent 이미지
                      </h4>
                      <div className="aspect-square bg-slate-800 rounded-lg overflow-hidden">
                        <img
                          src={createdAgent.agent_image}
                          alt={createdAgent.agent_title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23374151'/%3E%3Cg fill='%239CA3AF'%3E%3Cpath d='M100 60c-22.09 0-40 17.91-40 40s17.91 40 40 40 40-17.91 40-40-17.91-40-40-40zm0 60c-11.05 0-20-8.95-20-20s8.95-20 20-20 20 8.95 20 20-8.95 20-20 20z'/%3E%3Ccircle cx='100' cy='90' r='8'/%3E%3Cpath d='M85 110h30l-5-10h-20z'/%3E%3C/g%3E%3Ctext x='100' y='160' text-anchor='middle' fill='%239CA3AF' font-family='sans-serif' font-size='12'%3E이미지 로드 실패%3C/text%3E%3C/svg%3E";
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {savedError && (
                  <div className="bg-red-900/50 border border-red-400 text-red-100 px-4 py-3 rounded-lg">
                    <div className="flex items-center">
                      <AlertTriangleIcon className="w-5 h-5 mr-2" />
                      {savedError.includes("Duplicate entry")
                        ? "이미 저장된 Agent입니다. 다른 Agent를 생성하거나 편집 기능을 사용해주세요."
                        : savedError}
                    </div>
                  </div>
                )}

                {editError && (
                  <div className="bg-red-900/50 border border-red-400 text-red-100 px-4 py-3 rounded-lg">
                    <div className="flex items-center">
                      <AlertTriangleIcon className="w-5 h-5 mr-2" />
                      {editError}
                    </div>
                  </div>
                )}

                {templateFixError && (
                  <div className="bg-yellow-900/50 border border-yellow-400 text-yellow-100 px-4 py-3 rounded-lg">
                    <div className="flex items-center">
                      <AlertTriangleIcon className="w-5 h-5 mr-2" />
                      템플릿 재수정 실패: {templateFixError}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 테스트 모달 */}
      {isTestModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto custom-scrollbar">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={closeTestModal}
          />

          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-slate-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-slate-800">
              {/* 모달 헤더 */}
              <div className="flex items-center justify-between p-6 border-b border-slate-800">
                <h2 className="text-xl font-semibold text-white">
                  Agent 테스트
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={resetTest}
                    className="px-3 py-1 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors"
                  >
                    초기화
                  </button>
                  <button
                    onClick={closeTestModal}
                    className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <XIcon className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
              </div>

              {/* 모달 내용 */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                {createdAgent && (
                  <div className="space-y-6">
                    <div className="bg-slate-800 rounded-lg p-4">
                      <h3 className="text-lg font-medium text-white mb-2">
                        {createdAgent.agent_title}
                      </h3>
                      <p className="text-slate-300 text-sm">
                        {createdAgent.agent_description}
                      </p>
                    </div>

                    {/* 테스트 입력 폼 */}
                    <div>
                      <h3 className="text-lg font-medium text-white mb-4">
                        테스트 입력
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(
                          createdAgent.agent_schema.properties
                        ).map(([key, field]) => renderTestField(key, field))}
                      </div>
                    </div>

                    {/* 테스트 실행 버튼 */}
                    <div className="flex gap-4">
                      <button
                        onClick={testAgent}
                        disabled={!canRunTest() || isTestLoading}
                        className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white py-3 px-6 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        {isTestLoading ? (
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            테스트 중...
                          </div>
                        ) : (
                          "Agent 테스트 실행"
                        )}
                      </button>
                    </div>

                    {testError && (
                      <div className="bg-red-900/50 border border-red-400 text-red-100 px-4 py-3 rounded-lg">
                        <div className="flex items-center">
                          <AlertTriangleIcon className="w-5 h-5 mr-2" />
                          {testError}
                        </div>
                      </div>
                    )}

                    {/* 테스트 결과 */}
                    {testResult && (
                      <div className="bg-slate-800 rounded-lg p-6">
                        <h3 className="text-lg font-medium text-white mb-4">
                          테스트 결과
                        </h3>

                        {/* 생성된 텍스트 */}
                        {testResult.generated_text && (
                          <div className="mb-6">
                            <h4 className="text-white font-medium mb-2">
                              생성된 텍스트
                            </h4>
                            <div className="bg-slate-900 rounded-lg p-4 max-h-96 overflow-y-auto custom-scrollbar">
                              <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans leading-relaxed">
                                {testResult.generated_text}
                              </pre>
                            </div>
                          </div>
                        )}

                        {testResult.generated_images &&
                          testResult.generated_images.length > 0 && (
                            <div>
                              <h4 className="text-white font-medium mb-2">
                                생성된 이미지
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {testResult.generated_images.map(
                                  (imageUrl, index) => (
                                    <div
                                      key={index}
                                      className="bg-slate-900 rounded-lg p-2"
                                    >
                                      <img
                                        src={imageUrl}
                                        alt={`생성된 이미지 ${index + 1}`}
                                        className="w-full h-48 object-cover rounded-lg"
                                        onError={(e) => {
                                          e.currentTarget.src =
                                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23374151'/%3E%3Cg fill='%239CA3AF'%3E%3Cpath d='M100 60c-22.09 0-40 17.91-40 40s17.91 40 40 40 40-17.91 40-40-17.91-40-40-40zm0 60c-11.05 0-20-8.95-20-20s8.95-20 20-20 20 8.95 20 20-8.95 20-20 20z'/%3E%3Ccircle cx='100' cy='90' r='8'/%3E%3Cpath d='M85 110h30l-5-10h-20z'/%3E%3C/g%3E%3Ctext x='100' y='160' text-anchor='middle' fill='%239CA3AF' font-family='sans-serif' font-size='12'%3E이미지 로드 실패%3C/text%3E%3C/svg%3E";
                                        }}
                                      />
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <CommonSidebar />
    </div>
  );
}
